import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { FiUser, FiMail, FiPhone, FiTarget, FiActivity, FiMessageSquare, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { bookConsultation, getConsultationFormToken } from '../utils/api';
import Turnstile, { isTurnstileConfigured } from './Turnstile';

const ConsultationForm = ({ className = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    age: '',
    goal: 'weight_loss',
    current_weight: '',
    target_weight: '',
    message: '',
    // Honeypot. Invisible and unreachable for real users, so anything in it came
    // from an automated client that filled every input it found. Deliberately
    // named so browsers and password managers have no reason to autofill it.
    _gotcha: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Signed proof that the form was rendered, fetched on mount. The backend reads
  // the signed timestamp inside it to reject submissions that arrive faster than
  // a human could type.
  const [formToken, setFormToken] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);

  const loadFormToken = useCallback(async () => {
    try {
      const data = await getConsultationFormToken();
      setFormToken(data.form_token || '');
    } catch (error) {
      // Non-fatal here: surface it only if the user actually tries to submit.
      console.error('Failed to load form token:', error);
      setFormToken('');
    }
  }, []);

  useEffect(() => {
    loadFormToken();
  }, [loadFormToken]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s-()]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.goal) {
      newErrors.goal = 'Please select your goal';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
      return;
    }

    if (isTurnstileConfigured() && !turnstileToken) {
      setSubmitError('Please complete the verification check below before submitting.');
      return;
    }

    setLoading(true);
    try {
      // Numeric and optional fields are sent as-is; the backend parses and range
      // checks them (see validate_consultation_payload). Parsing here previously
      // turned a legitimate 0 into null via `parseInt(...) || null`.
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        age: formData.age,
        goal: formData.goal,
        current_weight: formData.current_weight,
        target_weight: formData.target_weight,
        message: formData.message.trim(),
        _gotcha: formData._gotcha,
        form_token: formToken,
        turnstile_token: turnstileToken
      };

      await bookConsultation(payload);
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to book consultation:', error);
      setSubmitError(error.error || 'Failed to book consultation. Please try again.');

      // Turnstile tokens are single-use and the form token may have expired, so
      // refresh both to keep a retry viable.
      setTurnstileToken('');
      turnstileRef.current?.reset();
      loadFormToken();
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`card p-8 text-center ${className}`}
      >
        <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <FiCheck className="w-8 h-8 text-secondary-600" />
        </div>
        <h3 className="text-2xl font-bold text-secondary-900 mb-4">
          Consultation Booked Successfully!
        </h3>
        <p className="text-secondary-600 mb-6">
          Thank you for booking a consultation with Deeksha. You'll receive a confirmation email shortly, 
          and we'll contact you within 24 hours to schedule your session.
        </p>
        <div className="space-y-3 text-sm text-secondary-500">
          <p>📧 Check your email for confirmation details</p>
          <p>📱 We'll contact you on {formData.phone}</p>
          <p>🎯 Goal: {formData.goal === 'weight_loss' ? 'Weight Loss' : 'Weight Gain'}</p>
        </div>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`space-y-6 ${className}`}>
      {/*
        Honeypot: positioned off-screen rather than display:none, since some bots
        skip fields that are explicitly hidden. aria-hidden and tabIndex=-1 keep
        it away from screen readers and keyboard navigation.
      */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      >
        <label htmlFor="consultation-gotcha">Leave this field empty</label>
        <input
          id="consultation-gotcha"
          type="text"
          name="_gotcha"
          value={formData._gotcha}
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      {/* Personal Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            <FiUser className="inline w-4 h-4 mr-1" />
            Full Name *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`input-field ${errors.name ? 'border-danger-600' : ''}`}
            placeholder="Enter your full name"
          />
          {errors.name && <p className="text-danger-700 text-sm mt-1 font-medium">{errors.name}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            <FiMail className="inline w-4 h-4 mr-1" />
            Email Address *
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`input-field ${errors.email ? 'border-danger-600' : ''}`}
            placeholder="Enter your email"
          />
          {errors.email && <p className="text-danger-700 text-sm mt-1 font-medium">{errors.email}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            <FiPhone className="inline w-4 h-4 mr-1" />
            Phone Number *
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`input-field ${errors.phone ? 'border-danger-600' : ''}`}
                            placeholder="+91 9355353459"
          />
          {errors.phone && <p className="text-danger-700 text-sm mt-1 font-medium">{errors.phone}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Age (Optional)
          </label>
          <input
            type="number"
            name="age"
            value={formData.age}
            onChange={handleChange}
            className="input-field"
            placeholder="Enter your age"
            min="16"
            max="80"
          />
        </div>
      </div>

      {/* Goal Selection */}
      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          <FiTarget className="inline w-4 h-4 mr-1" />
          Primary Goal *
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className={`card p-4 cursor-pointer transition-all duration-200 ${
            formData.goal === 'weight_loss' 
              ? 'ring-2 ring-primary-500 bg-primary-50' 
              : 'hover:bg-gray-50'
          }`}>
            <input
              type="radio"
              name="goal"
              value="weight_loss"
              checked={formData.goal === 'weight_loss'}
              onChange={handleChange}
              className="sr-only"
            />
            <div className="flex items-center space-x-3">
              <div className={`w-4 h-4 rounded-full border-2 ${
                formData.goal === 'weight_loss' 
                  ? 'border-primary-500 bg-primary-500' 
                  : 'border-gray-300'
              }`}>
                {formData.goal === 'weight_loss' && (
                  <div className="w-2 h-2 bg-white rounded-full mx-auto mt-1"></div>
                )}
              </div>
              <div>
                <h4 className="font-medium text-secondary-900">Weight Loss</h4>
                <p className="text-sm text-secondary-600">Lose weight healthily and sustainably</p>
              </div>
            </div>
          </label>

          <label className={`card p-4 cursor-pointer transition-all duration-200 ${
            formData.goal === 'weight_gain' 
              ? 'ring-2 ring-primary-500 bg-primary-50' 
              : 'hover:bg-gray-50'
          }`}>
            <input
              type="radio"
              name="goal"
              value="weight_gain"
              checked={formData.goal === 'weight_gain'}
              onChange={handleChange}
              className="sr-only"
            />
            <div className="flex items-center space-x-3">
              <div className={`w-4 h-4 rounded-full border-2 ${
                formData.goal === 'weight_gain' 
                  ? 'border-primary-500 bg-primary-500' 
                  : 'border-gray-300'
              }`}>
                {formData.goal === 'weight_gain' && (
                  <div className="w-2 h-2 bg-white rounded-full mx-auto mt-1"></div>
                )}
              </div>
              <div>
                <h4 className="font-medium text-secondary-900">Weight Gain</h4>
                <p className="text-sm text-secondary-600">Gain weight through proper nutrition</p>
              </div>
            </div>
          </label>
        </div>
        {errors.goal && <p className="text-danger-700 text-sm mt-1 font-medium">{errors.goal}</p>}
      </div>

      {/* Weight Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-2">
                  <FiActivity className="inline w-4 h-4 mr-1" />
                  Current Weight (kg)
          </label>
          <input
            type="number"
            name="current_weight"
            value={formData.current_weight}
            onChange={handleChange}
            className="input-field"
            placeholder="e.g., 70"
            min="30"
            max="200"
            step="0.1"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Target Weight (kg)
          </label>
          <input
            type="number"
            name="target_weight"
            value={formData.target_weight}
            onChange={handleChange}
            className="input-field"
            placeholder="e.g., 65"
            min="30"
            max="200"
            step="0.1"
          />
        </div>
      </div>

      {/* Additional Message */}
      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          <FiMessageSquare className="inline w-4 h-4 mr-1" />
          Additional Information (Optional)
        </label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={4}
          className="input-field resize-none"
          placeholder="Tell us about your lifestyle, dietary preferences, health conditions, or any specific concerns..."
        ></textarea>
      </div>

      {/* Bot verification. Renders nothing when no site key is configured. */}
      {isTurnstileConfigured() && (
        <div>
          <Turnstile
            ref={turnstileRef}
            onVerify={(token) => {
              setTurnstileToken(token);
              setSubmitError('');
            }}
            onExpire={() => {
              // Turnstile tokens expire after 5 minutes, which is easily less
              // than the time someone takes over an eight-field form. Re-run the
              // challenge immediately so a slow-but-genuine user is never left
              // holding a dead token.
              setTurnstileToken('');
              turnstileRef.current?.reset();
            }}
            onError={() => setTurnstileToken('')}
          />
        </div>
      )}

      {/* Submit error. Replaces the previous alert(), which was easy to miss and
          invisible to assistive tech. */}
      {submitError && (
        <div
          role="alert"
          className="flex items-start space-x-2 rounded-lg border border-danger-600 bg-red-50 p-4"
        >
          <FiAlertCircle className="w-5 h-5 text-danger-700 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-danger-700 font-medium">{submitError}</p>
        </div>
      )}

      {/* Submit Button */}
      <motion.button
        type="submit"
        disabled={loading}
        className={`w-full btn-primary flex items-center justify-center space-x-2 ${
          loading ? 'opacity-75 cursor-not-allowed' : ''
        }`}
        whileHover={!loading ? { scale: 1.02 } : {}}
        whileTap={!loading ? { scale: 0.98 } : {}}
      >
        {loading ? (
          <>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>Booking Consultation...</span>
          </>
        ) : (
          <>
            <FiCheck className="w-5 h-5" />
            <span>Book Free Consultation</span>
          </>
        )}
      </motion.button>

      <p className="text-xs text-secondary-500 text-center">
        By submitting this form, you agree to be contacted by our team for consultation scheduling.
        We respect your privacy and will never share your information.
      </p>
    </form>
  );
};

export default ConsultationForm;